import { ChangeEvent, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateProductMutation } from '@modules/product';
import { MESSAGES } from '@app/constant';
import { useRenderFileViewHook } from '@app/hooks/imagesRender';
import { throwImageFiltering } from '@app/helpers/uploadFileValidation';

import { createProductSchema, initialFormState } from './data';
import { handleDeleteImage, onChangeUpload } from './localHelper';
import { ICreateProductFormData } from './types';

export const useCreateProductLogic = () => {
  const [createProduct] = useCreateProductMutation()

  const [state, setState] = useState({
    isSuccess: '',
    largeImageMessage: '',
    isViewImageModalOpen: false,
    isButtonLoading: false,
    imageUrl: '',
    isDeleteModalOpen: false,
  })

  const methods = useForm<ICreateProductFormData>({
    // @ts-ignore
    resolver: yupResolver(createProductSchema),
    mode: 'onSubmit',
    defaultValues: initialFormState
  });

  const {
    reset,
    watch,
    register,
    setValue,
    setError,
    handleSubmit,
    formState: { errors }
  } = methods;

  const images = watch('images')
  const mainImage = watch('mainImage')
  const previews = useRenderFileViewHook(images)

  const onSubmit = async (data: ICreateProductFormData) => {
    setState((prev) => ({ ...prev, isButtonLoading: true, isSuccess: '' }))

    try {
      const imagesList = Object.values(data?.images || {})

      if (!images.length) {
        console.log('Error: ', Object.values(imagesList || {}));

        return
      }

      const formData = new FormData();
      const { descriptionEn, descriptionAm, descriptionRu } = data

      if (descriptionEn) {
        formData.append('descriptionEn', data.descriptionEn)
      }

      if (descriptionAm) {
        formData.append('descriptionAm', data.descriptionAm)
      }

      if (descriptionRu) {
        formData.append('descriptionRu', data.descriptionRu)
      }

      formData.append('titleEn', data.titleEn)
      formData.append('titleAm', data.titleAm)
      formData.append('titleRu', data.titleRu)
      formData.append('visible', data.visible)
      formData.append('brand', data.brand)

      for (let i = 0; i < imagesList.length; i++) {
        formData.append('files[]', imagesList[i])
      }

      await createProduct(formData).unwrap()

      reset()
      setState((prev) => ({
        ...prev,
        isButtonLoading: false,
        largeImageMessage: '',
        isSuccess: MESSAGES.productHasBeenSuccessfullyCreated
      }))
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        isButtonLoading: false,
        largeImageMessage: '',
        isSuccess: error?.data?.message || MESSAGES.somethingWentWrong
      }))
    }
  };

  useEffect(() => {
    if (!mainImage && images.length) {
      setValue('mainImage', '0')
    }
  }, [images, mainImage])

  return {
    ...state,
    methods,
    mainImage,
    images,
    errors,
    previews,
    watch,
    setState,
    onSubmit,
    register,
    handleSubmit,
    handleDeleteImage: (bas64: string) => handleDeleteImage({
      bas64,
      previews,
      setValue,
      setState,
      images,
    }),
    onChangeUpload: (event: ChangeEvent<HTMLInputElement>) => onChangeUpload({
      event,
      images,
      setValue,
      setState,
      setError,
    }),
  }
}
